import React from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppSelector } from '@/store';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { ResourceCard } from '@/components/residents/home/ResourceCard';
import { PropertyInfoCard } from '@/components/residents/home/PropertyInfoCard';
import {
  PADDING,
  TYPOGRAPHY,
  SCREEN_WIDTH,
} from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { homeStrings } from '@/constants/residents/strings';
import { CommunityResource } from '@/types/residents/communityResource';

// Background images for PropertyInfoCards
const contactOfficeBackground = require('@/assets/images/contact_office.jpg');
const residentHandbookBackground = require('@/assets/images/resident_handbook.jpg');

type MoreResourcesNavigationProp = StackNavigationProp<RootStackParamList, 'MoreResources'>;

export const MoreResourcesScreen: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const navigation = useNavigation<MoreResourcesNavigationProp>();
  const { resources } = useAppSelector((state) => state.home);
  const { user } = useAppSelector((state) => state.auth);
  const property = user?.property;

  // Check if handbook URL exists (matching iOS pattern from ContactOfficeScreen)
  const handbookUrl = property?.faqUrl || property?.websites?.handbook;
  const hasHandbookUrl = !!handbookUrl;

  // Filter resources with position > 3 (matching iOS logic)
  const moreResources = resources
    .filter((resource) => resource.position > 3);

  const handleResourcePress = (resource?: CommunityResource) => {
    if (resource) {
      navigation.navigate('ResourceDetail', { resource });
    }
  };

  const handleContactOfficePress = () => {
    // Navigate to Contact Office screen (matching iOS showContactOffice)
    navigation.navigate('ContactOffice');
  };

  const handleResidentHandbookPress = () => {
    // Navigate to FAQ WebView (matching iOS showFAQ)
    // Only called when handbookUrl exists
    if (handbookUrl) {
      navigation.navigate('WebView', { 
        url: handbookUrl, 
        title: homeStrings.faqs.title 
      });
    }
  };

  const renderResourceGrid = () => {
    if (moreResources.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No additional resources available</Text>
        </View>
      );
    }

    // Group resources into rows of 2 (matching iOS layout)
    const rows: CommunityResource[][] = [];
    for (let i = 0; i < moreResources.length; i += 2) {
      rows.push(moreResources.slice(i, i + 2));
    }

    return (
      <View style={styles.resourcesContainer}>
        {rows.map((row, rowIndex) => (
          <View key={rowIndex} style={styles.resourceRow}>
            {row.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                isInMoreResourcesScreen={true}
                onPress={handleResourcePress}
              />
            ))}
            {/* Add spacer if only one item in row */}
            {row.length === 1 && <View style={styles.resourceSpacer} />}
          </View>
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      
      {/* Property Info Cards (Contact Office & Resident Handbook) - matching iOS header */}
      <View style={styles.propertyInfoSection}>
        <View style={styles.propertyInfoRow}>
          <PropertyInfoCard
            title={homeStrings.propertyInfo.contactOffice}
            iconName="chatbubble-outline"
            backgroundImage={contactOfficeBackground}
            onPress={handleContactOfficePress}
          />
          {hasHandbookUrl && (
            <>
              <View style={styles.cardSpacer} />
              <PropertyInfoCard
                title={homeStrings.propertyInfo.residentHandbook}
                iconName="help-circle-outline"
                backgroundImage={residentHandbookBackground}
                onPress={handleResidentHandbookPress}
              />
            </>
          )}
        </View>
      </View>
      
      {renderResourceGrid()}
    </ScrollView>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  content: {
    padding: PADDING.padding4,
  },
  title: {
    ...TYPOGRAPHY.sectionTitle,
    color: colors.text.primary,
    marginBottom: PADDING.paddingHalf,
  },
  description: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.muted,
    marginBottom: PADDING.padding4,
  },
  resourcesContainer: {
    marginBottom: PADDING.padding4,
  },
  resourceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: PADDING.padding4,
  },
  resourceSpacer: {
    width: (SCREEN_WIDTH - PADDING.padding4 * 2 - PADDING.padding4) / 2, // Match ResourceCard width exactly
  },
  emptyContainer: {
    alignItems: 'center',
    padding: PADDING.padding8,
  },
  emptyText: {
    ...TYPOGRAPHY.cardDescription,
    color: colors.text.muted,
    textAlign: 'center',
  },
  propertyInfoSection: {
    marginBottom: PADDING.padding4,
  },
  propertyInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardSpacer: {
    width: PADDING.padding4, // Spacing between the two cards
  },
});